import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useRecordIcs201Version } from 'components/IAP/planningView/widgets/ics201VersionRecordingContext';
import config from '../../../constants/apiConfig';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { ObjectiveBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useActualIncidentId } from './commonHooks';
import { keys as incidentStrategyKeys } from './incidentStrategyHooks';
import {
  CreateObjectiveReqData,
  keys as objectiveKeys,
  useCreateObjective,
} from './objectiveHooks';

export type IncidentObjective = ObjectiveBase & {
  id: UUID;
  objective_id: UUID;
  incident_id: UUID;
  user_guid: UUID;
  version: number;
  timestamp: Date;
  archived: boolean;
  strategies: UUID[];
  user_name: string;
  /** Manual position within the incident's list. Lower sorts first. */
  display_order: number;
};

export const keys = {
  base: 'incident_objectives',
  get all() {
    return [keys.base];
  },
  list: (incidentId: UUID, userId: UUID) => [keys.base, incidentId, userId],
} as const;

export const useIncidentObjectives = <TData = IncidentObjective[]>(
  scope: IapScope = INCIDENT,
  selector?: (i: IncidentObjective[]) => TData
) => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, userGuid),
    queryFn: async () => {
      const resp = await axios.get<IncidentObjective[]>(
        `${config.apiGateway.ics_incident_objectives}/${incidentId}`
      );
      return resp.data.map((o) => ({
        ...o,
        timestamp: toUtcIso(o.timestamp) as IncidentObjective['timestamp'],
      }));
    },
    select: selector,
    // Guard on incidentId so callers outside an incident route (e.g. the DGPT approval modal at the hub) don't fetch
    // `/objectives/undefined`. Mirrors useIncidentRoster's `enabled: !!incidentId`.
    enabled: scope === INCIDENT && !!incidentId,
    throwOnError: (error) => {
      toast.error(error.message);
      return false;
    },
  });
};

/**
 * NOTE: this posts to the group objective which will create the incident
 * version.
 */
export const useCreateIncidentObjective = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useCreateObjective();
  const recordIcs201Version = useRecordIcs201Version();

  const invalidateCreatedObjective = () => {
    queryClient.invalidateQueries({ queryKey: objectiveKeys.all });
    queryClient.invalidateQueries({ queryKey: [keys.base] });
    recordIcs201Version();
  };

  return {
    ...mutation,
    mutateAsync: (data: CreateObjectiveReqData) => {
      data.incident_id = incidentId;
      const key = keys.list(incidentId, userGuid);
      const previous = queryClient.getQueryData<IncidentObjective[]>(key);
      const list = previous ?? [];
      const displayOrder =
        list.reduce(
          (max, objective) => Math.max(max, objective.display_order ?? 0),
          0
        ) + 1;
      const placeholderId = crypto.randomUUID();
      const placeholder: IncidentObjective = {
        name: data.name,
        description: data.description,
        type: data.type,
        strategies: data.strategies ?? [],
        id: placeholderId,
        objective_id: placeholderId,
        incident_id: (incidentId ?? '') as UUID,
        user_guid: userGuid,
        version: 0,
        timestamp: new Date(),
        archived: false,
        user_name: '',
        display_order: displayOrder,
      };
      // Same turn as the click. Waiting for the request left the table on
      // "0 objectives" long enough that the choice looked ignored.
      queryClient.setQueryData<IncidentObjective[]>(key, [
        ...list,
        placeholder,
      ]);

      let settled = false;
      const restore = () => {
        if (previous) {
          queryClient.setQueryData(key, previous);
          return;
        }
        queryClient.setQueryData<IncidentObjective[]>(key, (current) =>
          (current ?? []).filter(
            (objective) => objective.objective_id !== placeholderId
          )
        );
      };

      // A list refetch already in flight would replace the placeholder.
      void queryClient.cancelQueries({ queryKey: key }).then(() => {
        if (settled) return;
        const current = queryClient.getQueryData<IncidentObjective[]>(key);
        if (
          current?.some(
            (objective) => objective.objective_id === placeholderId
          )
        ) {
          return;
        }
        queryClient.setQueryData<IncidentObjective[]>(key, [
          ...(current ?? list),
          placeholder,
        ]);
      });

      return mutation
        .mutateAsync(data, {
          onSuccess: () => {
            settled = true;
            invalidateCreatedObjective();
          },
          onError: () => {
            settled = true;
            restore();
          },
        })
        .catch((error) => {
          settled = true;
          restore();
          throw error;
        });
    },
  };
};

/**
 * Renumbers the incident's objectives to the given order.
 *
 * A dedicated endpoint rather than a run of edits: the edit path inserts a new
 * version row, so saving a drag through it would leave a version behind for
 * every objective the move shifted.
 */
export const useReorderIncidentObjectives = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const recordIcs201Version = useRecordIcs201Version();

  return useMutation({
    mutationFn: async (objectiveIds: UUID[]) => {
      return await axios.post(
        `${config.apiGateway.ics_incident_objectives}/${incidentId}/reorder`,
        { objective_ids: objectiveIds }
      );
    },
    onMutate: async (objectiveIds) => {
      const key = keys.list(incidentId, userGuid);
      await queryClient.cancelQueries({ queryKey: key });

      const previous =
        queryClient.getQueryData<IncidentObjective[]>(key) ?? undefined;
      if (previous) {
        // Applied to the cache so the row settles where it was dropped instead
        // of springing back until the refetch lands.
        const byId = new Map(previous.map((o) => [o.objective_id, o]));
        const moved = objectiveIds
          .map((id, index) => {
            const objective = byId.get(id);
            return objective
              ? { ...objective, display_order: index + 1 }
              : undefined;
          })
          .filter((o): o is IncidentObjective => Boolean(o));
        const untouched = previous.filter(
          (o) => !objectiveIds.includes(o.objective_id)
        );
        queryClient.setQueryData(key, [...moved, ...untouched]);
      }

      return { previous, key };
    },
    onError: (error, _objectiveIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(error.message);
    },
    onSuccess: () => {
      recordIcs201Version();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export interface EditIncidentObjectiveReqData extends Partial<ObjectiveBase> {
  objective_id: UUID;
  archived: boolean;
  strategies: UUID[];
}

export type EditResponse<A> = {
  data: A;
  meta: {
    [key: string]: boolean;
  };
};

export const useEditIncidentObjective = (objectiveId?: UUID) => {
  //N.B.: The specific edit path this uses hits an INSERT rather than an UPDATE, so the user_guid is required despite this being an edit.
  // P.M., 2025-12-15
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const recordIcs201Version = useRecordIcs201Version();
  return useMutation({
    mutationFn: async (data: EditIncidentObjectiveReqData) => {
      return await axios.put<EditResponse<IncidentObjective>>(
        `${config.apiGateway.ics_incident_objectives}/${incidentId}/${objectiveId ?? data.objective_id}`,
        { ...data, user_guid }
      );
    },
    onSuccess: (resp, _) => {
      queryClient.invalidateQueries({
        queryKey: keys.all,
      });
      queryClient.invalidateQueries({
        queryKey: objectiveKeys.all,
      });
      if (resp.data.meta?.updatedStrategies) {
        queryClient.invalidateQueries({
          queryKey: incidentStrategyKeys.all,
        });
      }
      recordIcs201Version();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
